// src/i18n/enums.ts
// D-08: client-side localization of FINITE backend enums.
//
// These helpers map a stable backend enum CODE (+ optional role) to a localized
// display label via the i18n singleton. They use BARE FLAT keys in the single
// "common" namespace (no namespace-prefixed keys — defaultNS is "common") and fall
// back to the raw code with a defaultValue when a key is unknown.
//
// IMPORTANT (D-08 / Pitfall 6): only finite enums go through this layer. Free-text
// backend fields (names, message bodies, ministry details, report counts) are
// rendered RAW and must NEVER be passed through t(). The canonical
// `StatusesEnums.text`/`type` Hebrew strings in reportAssets/assets.ts remain the
// load-bearing identity used to MATCH backend-provided status strings (see
// getStatusColor / reportsBase merge logic); those matching values are intentionally
// not replaced — only the DISPLAY labels resolve through these helpers.
import i18n from "i18n";

/**
 * Localized label for a report status, keyed by the StatusesEnums `id` and the
 * viewing UserRole. Falls back to the raw status `id` when unknown.
 * Flat key shape: `enumsReportStatus_<id>_<role>`.
 */
export const tReportStatus = (id: number | string, role: number | string) =>
  i18n.t(`enumsReportStatus_${id}_${role}`, { defaultValue: String(id) });

/**
 * Localized label for a UserRole code (REGULAR=1, SENIOR=2, CONTROLCENTER=3,
 * SENIOR_WATCH=5). Falls back to the raw code.
 * Flat key shape: `enumsUserRole_<code>`.
 */
export const tUserRole = (code: number | string) =>
  i18n.t(`enumsUserRole_${code}`, { defaultValue: String(code) });

/**
 * Localized label for a license-plate type code (1..8). Falls back to the raw code.
 * Flat key shape: `enumsPlateType_<code>`.
 */
export const tPlateType = (code: number | string) =>
  i18n.t(`enumsPlateType_${code}`, { defaultValue: String(code) });

/**
 * Localized label for a decision-type code (DecisionTypesCode 1..7). Falls back to
 * the raw code.
 * Flat key shape: `enumsDecisionType_<code>`.
 */
export const tDecisionType = (code: number | string) =>
  i18n.t(`enumsDecisionType_${code}`, { defaultValue: String(code) });
